import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import InnerLoader from "../Shared/Loader/InnerLoader";
import { LanguagesMap } from "../OrganizationSettings/LanguagesMap";
import Loader from "../Shared/Loader/loader";
import Alice from "../Display/Alice";

const GenericTranslationDialog = ({
    onClose,
    onSave,
    languages = {},
    project,
    fields,
    type
}) => {
    const { t } = useTranslation("entity/label_translation_dialog", { useSuspense: false });
    const [saving, setSaving] = useState(false);
    const [translations, setTranslations] = useState({});
    const [languagesValid, setLanguagesValid] = useState(true);

    // Fetch the project data and initialize translations
    const fetchProject = useCallback(async () => {
        if (languages) {
            // Initialize translations state with existing language translations or empty strings
            setTranslations(
                Object.keys(languages).reduce((acc, key) => {
                    acc[key] = fields.reduce((fieldAcc, field) => {
                        fieldAcc[field] = languages[key]?.[field] || "";
                        return fieldAcc;
                    }, {});
                    return acc;
                }, {})
            );
        }
    }, [languages, fields]);

    // Use effect to fetch project data when the component mounts or dependencies change
    useEffect(() => {
        fetchProject();
    }, [fetchProject]);

    // Handle changes to the translation input fields
    const handleTranslationChange = (language, field, value) => {
        if (value.trim() === "") {
            setLanguagesValid(false);
        } else {
            setLanguagesValid(true);
        }
        // Update the translation for the specified language and field
        setTranslations((prevTranslations) => ({
            ...prevTranslations,
            [language]: {
                ...prevTranslations[language],
                [field]: value,
            },
        }));
    };

    const isLanguagesValid = (data) => {
        debugger;
        let isValid = true;
        for (let key in data) {
            for (let field of fields) {
                if (data[key][field].trim() === "") {
                    isValid = false;
                    break;
                }
            }
            if (!isValid) break;
        }
        return isValid;
    };

    // Handle save button click
    const handleSave = async () => {
        setSaving(true); // Set saving state to true to indicate loading
        const { schemaLanguages } = project;
        // Create an updatedLanguages object containing the updated translations
        const updatedLanguages = schemaLanguages.reduce((acc, languageCode) => {
            acc[languageCode] = {
                ...languages[languageCode],
                ...translations[languageCode],
            };
            return acc;
        }, {});

        // Check if the updated languages are valid
        if (!isLanguagesValid(updatedLanguages)) {
            setSaving(false); // Set saving state to false after saving
            setLanguagesValid(false);
            return;
        }
        // Call the onSave callback with the updated languages
        await onSave(updatedLanguages);
        setSaving(false); // Set saving state to false after saving
    };

    const isLanguagesChanged = (data) => {
        let isChanged = false;
        for (let key in data) {
            for (let field of fields) {
                if (data[key][field].trim() !== (languages[key]?.[field]?.trim() || "")) {
                    isChanged = true;
                    break;
                }
            }
            if (isChanged) break;
        }
        return isChanged;
    };

    return (
        <Dialog title={t("headers.title", { type })} onClose={onClose} width="60%" height="76%">
            {project ? (
                project.schemaLanguages.length > 0 ? (
                    <div className="container mt-3">
                        <div className="row mb-4">
                            <div className="col-4">
                                <strong>{t("headers.language")}</strong>
                            </div>
                            {fields.map((field, index) => (
                                <div className="col-4" key={index}>
                                    <strong>{t(`headers.${field}`)}</strong>
                                </div>
                            ))}
                        </div>
                        {project.schemaLanguages.map((languageCode, index) => {
                            const languageName = LanguagesMap.find((l) => l.code === languageCode)?.name || languageCode;
                            return (
                                <div className="row mb-3" key={index}>
                                    <div className="col-4">
                                        <label>{languageName}</label>
                                    </div>
                                    {fields.map((field, fieldIndex) => (
                                        <div className="col-4" key={fieldIndex}>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={translations[languageCode]?.[field] || ""}
                                                onChange={(e) => handleTranslationChange(languageCode, field, e.target.value)}
                                            />
                                        </div>
                                    ))}
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <Alice mode="new" header={t("no_data_message.header")}>
                        {t("no_data_message.message_1")}
                        <br />
                        <br />
                        {t("no_data_message.click_on")} &nbsp;
                        {t("no_data_message.add_properties")}
                    </Alice>
                )
            ) : (
                <Loader />
            )}
            <DialogActionsBar layout="end">
                {project && project.schemaLanguages.length > 0 && (
                    <div className="m-2">
                        {!languagesValid && (
                            <small className="text-danger me-2">{t("errors.invalid_language")}</small>
                        )}
                        {saving && (
                            <small className="mt-2">
                                <div className="d-inline-block">
                                    <InnerLoader borderColor="green" />
                                </div>
                            </small>
                        )}
                        <button className="btn btn-secondary" onClick={onClose}>
                            {t("headers.buttons.cancel")}
                        </button>
                        <button
                            autoFocus={true}
                            disabled={saving || !languagesValid || !isLanguagesChanged(translations)}
                            className="btn btn-primary me-2"
                            onClick={handleSave}
                        >
                            {t("headers.buttons.save")}
                        </button>
                    </div>
                )}
            </DialogActionsBar>
        </Dialog>
    );
};

export default GenericTranslationDialog;